
/**
 * Module dependecies.
 */

import { Icon } from 'react-components/media';
import { ImageSource } from 'slyk-web-libs/types';
import { Text, useImageSources } from 'slyk-web-libs/design-system';
import { color, media } from 'react-components/styles';
import { resolveImageUrl } from 'client/core/utils/image';
import { theme } from 'styled-tools';
import { useBreakpoint } from 'slyk-web-libs/breakpoint';
import React, { MouseEventHandler } from 'react';
import noImage40 from 'client/assets/svg/40/no-image.svg';
import noImage56 from 'client/assets/svg/56/no-image.svg';
import styled from 'styled-components';

/**
 * Export `TaskType` type.
 */

export type TaskType = {
  amount: string;
  amountDefaultCurrency: string;
  id: string;
  media: ImageSource | null | undefined;
  name: string;
  normalizedAmount: string;
};

/**
 * `Props` type.
 */

type Props = TaskType & {
  onClick: MouseEventHandler<HTMLButtonElement>;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.button`
  align-items: center;
  appearance: none;
  background-color: ${color('white')};
  border: none;
  border-radius: ${theme('dimensions.borderRadius')}px;
  cursor: pointer;
  display: grid;
  grid-template-columns: 80px 1fr;
  grid-template-rows: 80px;
  overflow: hidden;
  padding: 0;
  text-align: start;
  transition: background-color ${theme('animations.defaultTransition')};
  width: 100%;

  &:focus,
  &:hover {
    background-color: ${color('gray300')};
    outline: none;
  }
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  align-items: center;
  background-color: ${color('gray500')};
  border-radius: ${theme('dimensions.borderRadius')}px;
  display: flex;
  height: 100%;
  justify-content: center;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

/**
 * `Image` styled component.
 */

const Image = styled.img`
  object-fit: cover;
  object-position: center;
`;

/**
 * `Details` styled component.
 */

const Details = styled.div`
  overflow: hidden;
  padding: 7px 8px;

  ${media.min('ms')`
    padding: 16.5px 16px;
  `}
`;

/**
 * `Name` styled component.
 */

const Name = styled(Text)`
  color: ${color('gray800')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${media.min('ms')`
    padding-bottom: 5px;
  `}
`;

/**
 * Export `Task` component.
 */

export function Task(props: Props): JSX.Element {
  const { amountDefaultCurrency, media, name, normalizedAmount, onClick } = props;
  const isDesktop = useBreakpoint('ms', 'min');
  const imageSources = useImageSources(resolveImageUrl(media), '96x');

  return (
    <Wrapper
      onClick={onClick}
      type={'button'}
    >
      <ImageWrapper>
        {imageSources?.retinaUrl ? (
          <Image
            alt={name ?? ''}
            height={'100%'}
            loading={'lazy'}
            src={imageSources.retinaUrl}
            width={'100%'}
          />
        ) : (
          <Icon
            aria-hidden
            color={color('white')}
            icon={isDesktop ? noImage56 : noImage40}
            size={isDesktop ? '56px' : '40px'}
          />
        )}
      </ImageWrapper>

      <Details>
        <Name title={name}>
          {name}
        </Name>

        <Text
          color={color('blue600')}
          size={'small'}
        >
          {normalizedAmount}
        </Text>

        <Text
          color={color('grey800')}
          size={'extraSmall'}
        >
          {amountDefaultCurrency}
        </Text>
      </Details>
    </Wrapper>
  );
}
